import React, { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements
} from "@stripe/react-stripe-js";
import {
    Box,
    Typography,
    Button,
    Paper,
    Divider
} from "@mui/material";
import axios from "axios";
import { ACCESS_TOKEN_KEY, BASE_URL } from "../../utils/constants";
import { theme } from "../../theme";
import { useSnackbar } from "../../context/SnackbarContext";

const CheckoutForm = ({ booth }) => {
    const boothObj = JSON.parse(booth);
    const stripe = useStripe();
    const elements = useElements();
    const [message, setMessage] = useState("");

    const showSnackbar = useSnackbar();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        const result = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: `${window.location.origin}/_/events/manage/2`,
            },
        });

        if (result.error) {
            setMessage(result.error.message);
        } else if (result.paymentIntent?.status === "succeeded") {
            showSnackbar("🎉 Event Application was successful!", "success");
            setMessage("Payment succeeded!");
        }
    };

    return (
        <Box mt={-1}>
            <Paper elevation={0} sx={{ padding: 1 }}>
                <Box display="flex" flexDirection="column" gap={2} mb={2}>
                    <Box display="flex" justifyContent="space-between" gap={1}>
                        <Typography variant="subtitle1" fontWeight="bold">Base Cost</Typography>
                        <Typography variant="subtitle1">${boothObj.cost}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" gap={1}>
                        <Typography variant="subtitle1" fontWeight="bold">Platform & Payment Fees</Typography>
                        <Typography variant="subtitle1">${boothObj.platform_charge}</Typography>
                    </Box>
                    <Divider />
                    <Box display="flex" justifyContent="space-between">
                        <Typography variant="h6" fontWeight="bold">Total Cost</Typography>
                        <Typography variant="h6">${boothObj.total_cost}</Typography>
                    </Box>
                    <Typography variant="subtitle2" gutterBottom>
                        All sales are final. You won't be charged unless the organizer approves your application.
                    </Typography>
                </Box>
                <Box component="form" onSubmit={handleSubmit}>
                    <Box sx={{ marginBottom: 2 }}>
                        <PaymentElement />
                    </Box>
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={!stripe}
                        sx={{
                            borderRadius: 3,
                            textTransform: "capitalize",
                            fontWeight: 600,
                            boxShadow: 2,
                            px: 4,
                            bgcolor: theme.colors.events.primary,
                        }}
                    >
                        Pay
                    </Button>
                    {message && (
                        <Typography variant="body2" color="textSecondary" mt={2}>
                            {message}
                        </Typography>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};

const StripePayment = ({ slug, booth }) => {
    const [clientSecret, setClientSecret] = useState("");
    const [stripePromise, setStripePromise] = useState(null);

    useEffect(() => {
        const fetchClientSecret = async () => {
            try {
                const token = localStorage.getItem(ACCESS_TOKEN_KEY);
                const config = {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                };
                const { data } = await axios.post(
                    `${BASE_URL}/api/v1/events/stripe/create-payment-intent/`,
                    { event_listing_slug: slug },
                    config
                );

                setClientSecret(data.client_secret);
                setStripePromise(loadStripe(data.stripe_publishable_key));
            } catch (err) {
                console.error("Failed to fetch client secret", err);
            }
        };

        fetchClientSecret();
    }, [slug]);

    const appearance = { theme: "stripe" };
    const options = { clientSecret, appearance };

    if (!clientSecret || !stripePromise) {
        return <Typography mt={5} align="center">Loading payment form...</Typography>;
    }

    return (
        <Elements stripe={stripePromise} options={options}>
            <CheckoutForm booth={booth} />
        </Elements>
    );
};

export default StripePayment;
